import React, { useEffect, useRef, useState } from 'react'
import { StyledProfileItem } from 'src/workspace/components/Flyout/workspaceProfileStyles'
import { type Client } from 'shared/context/ClientContext'
import { type User } from 'shared/context/AuthContext'
import { useTranslation } from 'shared/translations'
import { Content } from './AvatarUploadContent'
import {
  handleAvatarUploadClick,
  validateFile,
  clearFileInput,
  handleValidationError,
  ACCEPTED_FILE_TYPES_STRING
} from './utils'
import FileUpload, { type FileUploadProps } from '@precis-digital/kurama/FileUpload'
import { makeToastWithLoading } from 'shared/components/Toaster'
import { type RequireAtLeastOne } from 'type-fest'

import {
  type ContentProps,
  type AvatarDisplayProps,
  type EditModeWithoutAvatarProps,
  type EditModeWithAvatarProps,
  type ViewModeProps
} from './AvatarUploadTypes'

interface InlineFlyoutAvatarUploadProps {
  client?: Omit<Client, 'featurePlan' | 'currency'>
  user?: User
  title?: string
  uploadAvatarProps: {
    uploadAvatar: (file: File) => Promise<void>
    isUploading: boolean
    isUploadSuccess: boolean
    onUploadSuccess?: () => void
  }
}

const InlineFlyoutAvatarUpload: React.FC<RequireAtLeastOne<InlineFlyoutAvatarUploadProps, 'client' | 'user'>> = ({
  client,
  title,
  uploadAvatarProps,
  user
}) => {
  const { t } = useTranslation('common')
  const { t: tWorkspace } = useTranslation('workspace')
  const { uploadAvatar, isUploading, isUploadSuccess, onUploadSuccess } = uploadAvatarProps
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [isAvatarSet, setIsAvatarSet] = useState<boolean>(true)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null)
  const [avatarVersion, setAvatarVersion] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (isUploadSuccess) {
      setAvatarVersion(Date.now())
    }
  }, [isUploadSuccess])

  const onUpload: FileUploadProps['onUpload'] = (files) => {
    if (files.length === 0) return

    const file = files[0]
    setUploadErrorMessage(null)
    const validationError = validateFile(file, t)

    if (validationError !== null) {
      handleValidationError({
        error: validationError,
        isEdit,
        t,
        setUploadErrorMessage,
        fileInputRef
      })
      return
    }

    const { toastOnSuccess, toastOnError } = makeToastWithLoading({
      headerMessage: t('toasts.uploadingImage')
    })

    uploadAvatar(file)
      .then(() => {
        setIsAvatarSet(true)
        onUploadSuccess?.()
        toastOnSuccess(t('toasts.imageUploadSuccess'))
      })
      .catch(() => {
        toastOnError(t('toasts.imageUploadFailed'))
      })
      .finally(() => {
        clearFileInput(fileInputRef)
      })
  }

  const onButtonClick = (): void => {
    handleAvatarUploadClick({
      fileInputRef,
      setUploadErrorMessage
    })
  }

  const handleOnImageError = (): void => {
    setIsAvatarSet(false)
  }

  const avatarDisplayProps: AvatarDisplayProps = {
    isAvatarSet,
    isUploadSuccess,
    handleOnImageError,
    client,
    user,
    t,
    avatarVersion
  }

  const editModeWithoutAvatarProps: EditModeWithoutAvatarProps = {
    title,
    uploadErrorMessage,
    t,
    isUploading,
    setIsEdit,
    setUploadErrorMessage,
    handleButtonClick: onButtonClick
  }

  const editModeWithAvatarProps: EditModeWithAvatarProps = {
    title,
    isUploading,
    handleButtonClick: onButtonClick,
    avatarDisplayProps
  }

  const viewModeProps: ViewModeProps = {
    title,
    isAvatarSet,
    isUploading,
    handleButtonClick: onButtonClick,
    setIsEdit,
    tWorkspace,
    avatarDisplayProps
  }

  const contentProps: ContentProps = {
    isEdit,
    isUploadSuccess,
    isAvatarSet,
    editModeWithAvatarProps,
    editModeWithoutAvatarProps,
    viewModeProps
  }

  return (
    <>
      <FileUpload
        name="avatar"
        placeholder={t('avatarUpload.uploadAvatar')}
        maxFiles={1}
        multiple={false}
        onUpload={onUpload}
        InputProps={{ ref: fileInputRef, accept: ACCEPTED_FILE_TYPES_STRING }}
        style={{ display: 'none' }}
      />
      <StyledProfileItem>
        <Content {...contentProps} />
      </StyledProfileItem>
    </>
  )
}

export default InlineFlyoutAvatarUpload
